/**
 * Accueil du back-office master : une carte par section, regroupées par thème.
 * La carte LegalFlow affiche l'état des pipelines en direct.
 */
import { ArrowRight } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import Page from 'pages/Page';

import { ChainlitContext, useAuth } from '@chainlit/react-client';

import {
  ADMIN_GROUPS,
  type AdminSection,
  staffAccess
} from '@/components/admin/sections';

interface FlowSummary {
  total: number;
  failing: number;
  running: number;
  suspended: number;
}

function SectionCard({
  section,
  extra
}: {
  section: AdminSection;
  extra?: React.ReactNode;
}) {
  const Icon = section.icon;
  return (
    <Link
      to={section.path}
      className="group relative flex flex-col rounded-2xl border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between gap-3">
        <span
          className={`inline-flex h-11 w-11 items-center justify-center rounded-xl ${section.accent}`}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
        {section.badge && (
          <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
            {section.badge}
          </span>
        )}
      </div>
      <h3 className="mt-4 text-base font-semibold text-foreground">
        {section.title}
      </h3>
      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
        {section.description}
      </p>
      {extra && <div className="mt-4">{extra}</div>}
      <span className="mt-auto flex items-center gap-1 pt-4 text-xs font-medium text-primary opacity-0 transition-opacity group-hover:opacity-100">
        Ouvrir <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
    </Link>
  );
}

function FlowPills({ summary }: { summary: FlowSummary | null | 'error' }) {
  if (summary === null)
    return <div className="h-6 w-40 animate-pulse rounded-full bg-muted" />;
  if (summary === 'error')
    return <p className="text-xs text-muted-foreground">État indisponible</p>;
  const pill = 'rounded-full px-2.5 py-1 text-xs font-medium';
  return (
    <div className="flex flex-wrap gap-1.5">
      <span className={`${pill} bg-muted text-foreground`}>
        {summary.total} pipelines
      </span>
      {summary.running > 0 && (
        <span
          className={`${pill} bg-blue-500/10 text-blue-700 dark:text-blue-300`}
        >
          {summary.running} en cours
        </span>
      )}
      {summary.failing > 0 ? (
        <span
          className={`${pill} bg-red-500/10 text-red-700 dark:text-red-300`}
        >
          {summary.failing} en erreur
        </span>
      ) : (
        <span
          className={`${pill} bg-emerald-500/10 text-emerald-700 dark:text-emerald-300`}
        >
          Aucune erreur
        </span>
      )}
      {summary.suspended > 0 && (
        <span className={`${pill} bg-muted text-muted-foreground`}>
          {summary.suspended} suspendus
        </span>
      )}
    </div>
  );
}

export default function AdminHome() {
  const apiClient = useContext(ChainlitContext);
  const { user, isAuthenticated, isReady } = useAuth();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<FlowSummary | null | 'error'>(null);
  const access = staffAccess(user?.metadata);
  const groups = ADMIN_GROUPS.map((g) => ({
    ...g,
    sections: g.sections.filter(access.canSee)
  })).filter((g) => g.sections.length > 0);
  const showFlow = groups.some((g) =>
    g.sections.some((s) => s.path === '/admin/legalflow')
  );

  useEffect(() => {
    if (isReady && !isAuthenticated) navigate('/login');
  }, [isReady, isAuthenticated, navigate]);

  useEffect(() => {
    if (!showFlow) return;
    fetch(apiClient.buildEndpoint('/api/legalflow/overview'), {
      credentials: 'include'
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((d) => {
        const p = d.pipelines as { health: string }[];
        setFlow({
          total: p.length,
          failing: p.filter(
            (x) => x.health === 'failing' || x.health === 'stuck'
          ).length,
          running: p.filter((x) => x.health === 'running').length,
          suspended: p.filter((x) => x.health === 'suspended').length
        });
      })
      .catch(() => setFlow('error'));
  }, [apiClient, showFlow]);

  const firstName = (user?.display_name || user?.identifier || '').split(
    /[ @.]/
  )[0];

  return (
    <Page>
      <div className="w-full overflow-y-auto">
        <section className="relative overflow-hidden border-b bg-gradient-to-br from-violet-600/10 via-background to-sky-500/10">
          <div className="mx-auto max-w-6xl px-6 py-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-violet-600 dark:text-violet-300">
              Back-office SmartLawyer
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-foreground">
              {firstName ? `Bonjour ${firstName}` : 'Bonjour'}
            </h1>
            <p className="mt-2 max-w-2xl text-muted-foreground">
              Pilotez les corpus, les pipelines d'ingestion, les cabinets
              clients et la croissance depuis un seul endroit.
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-6xl space-y-10 px-6 py-8">
          {groups.map((group) => (
            <section key={group.title} aria-labelledby={`grp-${group.title}`}>
              <div className="mb-4">
                <h2
                  id={`grp-${group.title}`}
                  className="text-lg font-semibold text-foreground"
                >
                  {group.title}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {group.subtitle}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {group.sections.map((s) => (
                  <SectionCard
                    key={s.path}
                    section={s}
                    extra={
                      s.path === '/admin/legalflow' ? (
                        <FlowPills summary={flow} />
                      ) : undefined
                    }
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </Page>
  );
}
